import { useEffect, useState } from "react";
import { track } from "../lib/analytics";

const APP_STORE_URL = "https://apps.apple.com/us/app/clarity-bible/id6783510260";
// Set this once your Android app is public on Google Play, e.g.
// "https://play.google.com/store/apps/details?id=host.emergent.scripture_focus.twa"
const PLAY_STORE_URL = "";

function detectPlatform() {
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua) || (ua.includes("Mac") && "ontouchend" in document)) return "ios";
  if (/Android/.test(ua)) return "android";
  return "other";
}

// Apple App Store supports a campaign token via ?ct= for attribution.
function appStoreUrl(campaign) {
  if (!campaign) return APP_STORE_URL;
  const sep = APP_STORE_URL.includes("?") ? "&" : "?";
  return `${APP_STORE_URL}${sep}ct=${encodeURIComponent(campaign.slice(0, 40))}`;
}

export default function Download() {
  const [platform] = useState(detectPlatform);
  const [redirecting, setRedirecting] = useState(false);

  const params = new URLSearchParams(window.location.search);
  const utm = {
    source: params.get("utm_source") || "",
    medium: params.get("utm_medium") || "",
    campaign: params.get("utm_campaign") || params.get("ct") || "",
  };
  const iosUrl = appStoreUrl(utm.campaign);

  // Fire the Google Ads conversion, then navigate once it's recorded (with a
  // safety timeout so a blocked/slow beacon never traps the user).
  const reportConversion = (url) => {
    let navigated = false;
    const go = () => { if (!navigated) { navigated = true; window.location.href = url; } };
    try {
      if (typeof window.gtag === "function") {
        window.gtag("event", "conversion", {
          send_to: "AW-18311181889/W1QECNvZzc0cEMHsuZtE",
          event_callback: go,
        });
        setTimeout(go, 1200);
      } else {
        go();
      }
    } catch { go(); }
  };

  useEffect(() => {
    track("download_page_view", { platform, ...utm });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Send store visitors on their way right away. The redirect IS the conversion,
  // so count it (manual taps also fire this via goApple, distinguished by `auto`).
  useEffect(() => {
    if (platform === "ios") {
      setRedirecting(true);
      track("appstore_click", { auto: true, platform, ...utm });
      reportConversion(iosUrl);
    } else if (platform === "android" && PLAY_STORE_URL) {
      setRedirecting(true);
      track("playstore_click", { auto: true, platform, ...utm });
      reportConversion(PLAY_STORE_URL);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goApple = () => { track("appstore_click", { auto: false, ...utm }); reportConversion(iosUrl); };
  const goPlay = () => { track("playstore_click", { auto: false, ...utm }); reportConversion(PLAY_STORE_URL); };

  const showApple = platform !== "android";
  const showPlay = !!PLAY_STORE_URL && platform !== "ios";
  // Android visitor with no public Play listing yet — show a friendly notice
  // (and still offer the App Store) instead of a dead-end page.
  const androidComingSoon = platform === "android" && !PLAY_STORE_URL;

  return (
    <div
      data-testid="download-page"
      className="fixed inset-0 flex flex-col items-center justify-center overflow-y-auto px-6 py-10 text-center text-[#FDEBD0]"
      style={{ background: "radial-gradient(125% 80% at 50% 0%, #26384e 0%, #131a33 45%, #0b1020 100%)" }}
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-1/2" style={{ background: "radial-gradient(55% 60% at 50% 0%, rgba(224,184,92,0.18), transparent 70%)" }} />

      <div className="relative z-10 flex w-full max-w-md flex-col items-center">
        <div className="mb-7 flex h-28 w-28 items-center justify-center rounded-[1.75rem] border border-[#E0B85C]/30 bg-white/[0.06] shadow-[0_10px_50px_rgba(224,184,92,0.35)] backdrop-blur-sm">
          <img src="/logo_mark.png" alt="Clarity Bible" className="h-20 w-20 object-contain" />
        </div>

        <h1 className="font-serif-display bg-gradient-to-b from-white to-[#F2D9A0] bg-clip-text text-4xl leading-tight text-transparent sm:text-5xl">
          Clarity Bible
        </h1>
        <p className="font-ui mt-4 text-base leading-relaxed text-[#D8DCEA]">
          A calm, focus-friendly Bible built for wandering minds — visual reading, word study, devotionals, and more.
        </p>

        <div className="mt-9 flex w-full flex-col gap-3">
          {showApple && (
            <button
              onClick={goApple}
              data-testid="appstore-button"
              className="font-ui flex w-full items-center justify-center gap-3 rounded-2xl bg-white py-4 text-lg font-bold text-[#0b1020] shadow-lg transition-transform hover:scale-[1.02]"
            >
              <svg viewBox="0 0 384 512" className="h-6 w-6 fill-current" aria-hidden="true">
                <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
              </svg>
              Download on the App Store
            </button>
          )}
          {showPlay && (
            <button
              onClick={goPlay}
              data-testid="playstore-button"
              className="font-ui flex w-full items-center justify-center gap-3 rounded-2xl border border-white/20 bg-white/[0.06] py-4 text-lg font-bold text-white backdrop-blur-sm transition-transform hover:scale-[1.02]"
            >
              <svg viewBox="0 0 512 512" className="h-6 w-6 fill-current" aria-hidden="true">
                <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z" />
              </svg>
              Get it on Google Play
            </button>
          )}
        </div>

        {androidComingSoon && (
          <div className="mt-6 rounded-2xl border border-white/15 bg-white/[0.05] p-5" data-testid="android-coming-soon">
            <p className="font-ui text-base font-semibold text-[#FDEBD0]">Coming soon to Google Play 🎉</p>
            <p className="font-ui mt-1.5 text-sm text-[#FDEBD0]/70">
              The Android version is on its way. It’s live now on iPhone &amp; iPad.
            </p>
          </div>
        )}

        {redirecting && (
          <p className="font-ui mt-4 text-sm text-[#FDEBD0]/60" data-testid="redirecting-note">
            Taking you to the store…
          </p>
        )}
        <p className="font-ui mt-6 text-xs text-[#FDEBD0]/45">Free to download</p>
      </div>
    </div>
  );
}
